/**
 * Rubato product card gallery.
 *
 * 1. The first to second image swap on hover is a 300ms crossfade rather than a cut. Both
 *    images sit in the same box and one fades into the other; nothing moves and nothing
 *    scales. Under prefers-reduced-motion the CSS drops the transition and the swap is
 *    instant. This runs on every card: the collection grid, Latest, You may also like,
 *    search and the journal's captions.
 *
 * 2. On the collection grid above 768 two chevrons step through the whole set, wrapping at
 *    either end, on the same crossfade.
 *
 * Nothing here is asked for before it is needed: the second photograph is requested on the
 * first hover or when the card reaches the viewport, whichever comes first, and the third
 * onwards only when a chevron reaches it.
 *
 * Round 44.
 */

const GALLERY = '[data-rubato-card-gallery]';
const DESKTOP = '(min-width: 768px)';

/** The slides of one gallery, in order. */
function slidesOf(gallery) {
  return [...gallery.querySelectorAll('slideshow-slide')];
}

/**
 * Puts back the source a slide was rendered without. The third image onwards ships with its
 * src and srcset parked in data attributes so the browser has nothing to fetch until the
 * chevrons reach it.
 */
function wake(slide) {
  if (!slide || slide.dataset.rubatoWoken === 'true') return;
  slide.dataset.rubatoWoken = 'true';

  for (const img of slide.querySelectorAll('img')) {
    const srcset = img.getAttribute('data-rubato-srcset');
    const src = img.getAttribute('data-rubato-src');
    if (srcset) {
      img.setAttribute('srcset', srcset);
      img.removeAttribute('data-rubato-srcset');
    }
    if (src) {
      img.setAttribute('src', src);
      img.removeAttribute('data-rubato-src');
    }
    // A lazy image inside a card that is already on screen should not wait for a scroll.
    img.removeAttribute('loading');
  }
}

const TRACK = '(max-width: 767px)';

/** True while the card is the phone's snap track rather than a stack. */
function isTrack(gallery) {
  return gallery.hasAttribute('data-rubato-steps') && window.matchMedia(TRACK).matches;
}

/**
 * The indicator: one line sliding across the picture's width in proportion to how far the
 * track has been pushed, rather than stepping between positions.
 *
 * The collection card and the product gallery both render the same snippet, and this finds
 * the track by looking inside the indicator's own parent, so neither needs to be named here.
 * Round 45 item 3, made common in round 49.
 */
function setUpIndicator(line) {
  if (line.dataset.rubatoReady === 'true') return;
  line.dataset.rubatoReady = 'true';

  const scope = line.parentElement;
  const track = scope?.querySelector('slideshow-slides');
  if (!track) return;

  const gallery = line.closest('[data-rubato-card-gallery]');
  let woken = false;

  const paint = () => {
    const travel = track.scrollWidth - track.clientWidth;
    const progress = travel > 0 ? track.scrollLeft / travel : 0;
    const room = track.clientWidth - line.offsetWidth;
    line.style.setProperty('--rubato-indicator-x', `${Math.round(progress * room * 100) / 100}px`);
  };

  track.addEventListener(
    'scroll',
    () => {
      /* On a card the rest of the set is asked for the moment the finger moves, not before.
         The product gallery ships its own images and has nothing parked. */
      if (!woken && gallery) {
        woken = true;
        for (const slide of slidesOf(gallery)) wake(slide);
      }
      paint();
    },
    { passive: true }
  );

  window.addEventListener('resize', paint);
  paint();
}

/** Shows one slide and hides the rest. The CSS does the fading. */
function show(gallery, index) {
  const slides = slidesOf(gallery);
  if (!slides.length) return;

  const next = (index + slides.length) % slides.length;
  wake(slides[next]);

  /* On the phone's snap track every photograph is on screen in turn, so none of them is
     hidden from anything. Stacked, only the one on top is readable. */
  const stacked = !isTrack(gallery);

  slides.forEach((slide, i) => {
    const on = i === next;
    slide.toggleAttribute('data-rubato-on', on);
    slide.setAttribute('aria-hidden', stacked && !on ? 'true' : 'false');
  });

  gallery.dataset.rubatoIndex = String(next);
}

function currentIndex(gallery) {
  return Number(gallery.dataset.rubatoIndex ?? 0);
}

function step(gallery, by) {
  show(gallery, currentIndex(gallery) + by);
}

function setUpGallery(gallery) {
  if (gallery.dataset.rubatoReady === 'true') return;

  const slides = slidesOf(gallery);
  if (slides.length < 2) {
    gallery.dataset.rubatoReady = 'true';
    return;
  }

  gallery.dataset.rubatoReady = 'true';
  show(gallery, 0);

  /* The second photograph, on the first hover or when the card reaches the viewport,
     whichever is first. */
  const second = slides[1];
  const wakeSecond = () => wake(second);

  /* A phone has no hover and its card keeps the one image, so fetching the second there is
     bytes spent on something the reader can never reach. The prefetch is for pointers. */
  const canHover = window.matchMedia('(hover: hover)').matches;

  if (canHover && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          wakeSecond();
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(gallery);
  }

  const card = gallery.closest('.product-card') ?? gallery;

  card.addEventListener('pointerenter', (event) => {
    if (event.pointerType !== 'mouse') return;
    wakeSecond();
    show(gallery, 1);
  });

  card.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    show(gallery, 0);
  });

  /* The chevrons, where the collection grid rendered them. */
  const steppers = [...gallery.querySelectorAll('[data-rubato-card-step]')];
  if (!steppers.length) return;

  for (const button of steppers) {
    const by = Number(button.dataset.rubatoCardStep);

    /* The card carries a click handler that opens the product, and Horizon lays a link over
       the whole card, so the chevron has to keep its click to itself. */
    const swallow = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    button.addEventListener('pointerdown', swallow);
    button.addEventListener('click', (event) => {
      swallow(event);
      if (!window.matchMedia(DESKTOP).matches) return;
      step(gallery, by);
    });

    button.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      event.stopPropagation();
      step(gallery, event.key === 'ArrowRight' ? 1 : -1);
    });

    /* A chevron reached by the keyboard shows the set without a pointer ever arriving. */
    button.addEventListener('focus', wakeSecond);
  }
}

function init(root = document) {
  for (const gallery of root.querySelectorAll(GALLERY)) setUpGallery(gallery);

  /* Wherever the indicator was rendered, card or product gallery. */
  for (const line of root.querySelectorAll('[data-rubato-indicator]')) setUpIndicator(line);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}

/* The grid re-renders on filter, sort and load more. */
let pending = false;
new MutationObserver(() => {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    init();
  });
}).observe(document.body, { childList: true, subtree: true });
